import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthProvider";
import { api } from "@/lib/api";
import { isEnabled } from "@/lib/featureFlags";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Check, X, Sparkles, Loader2, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const featureLabels = {
  wardrobe: "Wardrobe",
  outfit_per_day: "Outfit generations",
  tryon_per_day: "AI Try-Ons",
  stylist_chat_per_day: "Stylist chat",
  community: "Social",
  shopping: "Shopping insights",
  calendar: "Calendar",
  travel: "Travel planner",
  family: "Family",
  weekly_rewards: "Weekly rewards",
  priority_ai: "Priority AI",
};

const PURCHASABLE = new Set(["premium_monthly", "premium_annual", "day_pass", "travel_pass", "family"]);

export default function Plans() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [rpReady, setRpReady] = useState(false);
  const [rpMode, setRpMode] = useState(null); // 'test' | 'live' | null
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [dialogPlan, setDialogPlan] = useState(null);
  const [travelDays, setTravelDays] = useState(3);

  useEffect(() => {
    api.get("/plans/comparison").then(({ data }) => setPlans(data));
    api.get("/payments/razorpay/config").then(({ data }) => {
      setRpReady(!!data.ready);
      if (data.key_id?.startsWith("rzp_live_")) setRpMode("live");
      else if (data.key_id?.startsWith("rzp_test_")) setRpMode("test");
      else setRpMode(null);
    }).catch(() => {});
  }, []);

  const startStripe = async (plan_id, options = {}) => {
    setLoadingPlan(`stripe:${plan_id}`);
    try {
      const { data } = await api.post("/payments/checkout", {
        plan_id, origin_url: window.location.origin, options,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setLoadingPlan(null);
    }
  };

  const startRazorpay = async (plan_id, options = {}) => {
    if (!rpReady) {
      if (isEnabled("STRIPE_UI")) {
        toast.info("Razorpay test keys not configured yet — using Stripe instead.");
        return startStripe(plan_id, options);
      }
      toast.error("Razorpay is not configured — contact support.");
      return;
    }
    setLoadingPlan(`razorpay:${plan_id}`);
    try {
      const { data: cfg } = await api.get("/payments/razorpay/config");
      const { data: order } = await api.post("/payments/razorpay/order", { plan_id, options });
      await loadRazorpayScript();
      const rzp = new window.Razorpay({
        key: cfg.key_id, amount: order.amount, currency: order.currency,
        name: "Stylebay", description: order.plan_label, order_id: order.order_id,
        handler: async (resp) => {
          try {
            await api.post("/payments/razorpay/verify", resp);
            await refresh();
            toast.success("Subscription active");
            navigate("/profile");
          } catch { toast.error("Payment verification failed"); }
        },
        modal: { ondismiss: () => setLoadingPlan(null) },
        theme: { color: "#0a0a0a" },
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Razorpay could not start");
      setLoadingPlan(null);
    }
  };

  const loadRazorpayScript = () => new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve; s.onerror = () => reject(new Error("Razorpay script failed"));
    document.body.appendChild(s);
  });

  const handleSubscribe = (plan) => {
    if (!PURCHASABLE.has(plan.id)) return;
    if (plan.id === "travel_pass") { setDialogPlan(plan); return; }
    if (plan.id === "family") {
      toast.info("Create your family first, then return to Plans.");
      navigate("/family");
      return;
    }
    setDialogPlan(plan);
  };

  const renderValue = (v) => {
    if (v === true) return <Check size={14} className="text-accent" />;
    if (v === false) return <X size={14} className="text-muted-foreground opacity-40" />;
    return <span className="text-xs">{v}</span>;
  };

  // Resolve the user's current plan for the "active plan" banner.
  const activeTierId = user?.subscription_tier || "free";
  const activePlan = plans.find((p) => p.id === activeTierId);
  const activeLabel =
    activePlan?.label ||
    (activeTierId === "free" ? "Free" : activeTierId.charAt(0).toUpperCase() + activeTierId.slice(1));
  const expiresAt = user?.subscription_expires_at;
  const expiryText = expiresAt
    ? (() => {
        const d = new Date(expiresAt);
        const now = new Date();
        const days = Math.max(0, Math.ceil((d - now) / (1000 * 60 * 60 * 24)));
        return { label: d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }), days };
      })()
    : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-16">
      <p className="eyebrow flex items-center gap-2"><Sparkles size={12} /> Plan comparison</p>
      <h1 className="editorial-h1 mt-3">Choose the right fit.</h1>
      <p className="mt-4 text-sm text-muted-foreground max-w-xl">
        Every plan is built around how often you wear Stylebay — from a one-day try to your whole family.
      </p>

      {/* Active plan banner */}
      <div
        className="mt-8 border border-border bg-accent/5 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4"
        data-testid="plans-active-banner"
      >
        <div className="flex-1 min-w-0">
          <p className="eyebrow">Your active plan</p>
          <div className="mt-2 flex items-baseline gap-3 flex-wrap">
            <p className="font-serif text-2xl sm:text-3xl">{activeLabel}</p>
            {activeTierId !== "free" && (
              <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-accent text-accent">
                Active
              </span>
            )}
            {activeTierId === "free" && (
              <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-border text-muted-foreground">
                Free tier
              </span>
            )}
          </div>
          {expiryText ? (
            <p className="mt-1.5 text-xs text-muted-foreground">
              Renews · {expiryText.label}
              {expiryText.days <= 30 && (
                <span className={`ml-2 ${expiryText.days <= 7 ? "text-rose-500" : "text-amber-500"} font-semibold`}>
                  {expiryText.days === 0 ? "expires today" : `${expiryText.days}d left`}
                </span>
              )}
            </p>
          ) : (
            <p className="mt-1.5 text-xs text-muted-foreground">
              {activeTierId === "free"
                ? "Upgrade any time — pick a plan below."
                : "Lifetime access — no renewal needed."}
            </p>
          )}
        </div>
        {activeTierId !== "free" && (
          <button
            type="button"
            onClick={() => navigate("/profile")}
            className="text-[11px] uppercase tracking-[0.2em] px-4 h-10 border border-border hover:border-accent hover:text-accent transition"
            data-testid="plans-manage-btn"
          >
            Manage billing
          </button>
        )}
      </div>

      {/* Plan cards (purchase) */}
      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.filter((p) => p.id !== "free").map((p) => (
          <div key={p.id} data-testid={`plan-card-${p.id}`}
               className={`border p-6 flex flex-col ${p.featured ? "bg-accent text-accent-foreground border-foreground" : "border-border"}`}>
            <p className="text-[10px] uppercase tracking-[0.3em] opacity-80">{p.label}</p>
            <p className="font-serif text-4xl mt-3 tracking-tight">{p.price}</p>
            <p className="text-[10px] uppercase tracking-[0.2em] opacity-70 mt-1">{p.period}</p>
            <ul className="mt-5 space-y-1.5 text-sm flex-1">
              {Object.entries(featureLabels).slice(0, 6).map(([k, label]) => (
                <li key={k} className="flex items-start gap-2 text-[11px]">
                  {p.features[k] ? <Check size={11} className="mt-1 opacity-80" /> : <X size={11} className="mt-1 opacity-30" />}
                  <span><b>{label}:</b> {String(p.features[k])}</span>
                </li>
              ))}
            </ul>
            <Button
              onClick={() => handleSubscribe(p)}
              disabled={loadingPlan?.endsWith(`:${p.id}`)}
              data-testid={`plan-buy-${p.id}`}
              className={`mt-6 rounded-none uppercase tracking-[0.2em] text-xs h-11 ${
                p.featured ? "bg-background text-foreground hover:bg-background/90" : ""
              }`}>
              {loadingPlan?.endsWith(`:${p.id}`) ? <Loader2 size={14} className="animate-spin" /> :
                p.id === "family" ? "Set up family" : "Subscribe"}
            </Button>
          </div>
        ))}
      </div>

      {/* Comparison table */}
      <div className="mt-12 overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className="text-left p-4 w-44 border-b border-border"></th>
              {plans.map((p) => (
                <th key={p.id} data-testid={`plan-col-${p.id}`}
                    className={`text-left p-4 border-b border-border align-top min-w-[140px] ${p.featured ? "bg-accent text-accent-foreground" : ""}`}>
                  <p className="text-[10px] uppercase tracking-[0.3em] opacity-80">{p.label}</p>
                  <p className="font-serif text-3xl mt-2 tracking-tight">{p.price}</p>
                  <p className="text-[10px] uppercase tracking-[0.2em] opacity-70 mt-1">{p.period}</p>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(featureLabels).map(([key, label]) => (
              <tr key={key} className="border-b border-border">
                <td className="p-4 text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</td>
                {plans.map((p) => (
                  <td key={p.id} className={`p-4 ${p.featured ? "bg-accent/5" : ""}`}>
                    {renderValue(p.features[key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-6 text-[11px] text-muted-foreground">
        Secure payments powered by Razorpay {rpReady ? (rpMode === "live" ? "· live mode" : "· test mode active") : "· awaiting keys"}.
      </p>

      {/* Plan checkout dialog */}
      <Dialog open={!!dialogPlan} onOpenChange={(o) => !o && setDialogPlan(null)}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl tracking-tight">{dialogPlan?.label}</DialogTitle>
          </DialogHeader>
          {dialogPlan?.id === "travel_pass" && (
            <div>
              <Label className="eyebrow">Trip length (days)</Label>
              <Input data-testid="travel-pass-days-input" type="number" min={1} max={30}
                     value={travelDays} onChange={(e) => setTravelDays(Math.max(1, Math.min(30, Number(e.target.value) || 1)))}
                     className="rounded-none mt-2 h-11" />
              <p className="text-sm mt-3">
                Total: <b>₹{travelDays * 19}</b> for {travelDays} day{travelDays > 1 ? "s" : ""}
              </p>
            </div>
          )}
          {dialogPlan?.id === "day_pass" && (
            <p className="text-sm">24-hour access to all core AI features — outfit generation, stylist chat, Try-On. Community &amp; travel excluded.</p>
          )}
          <div className={`grid gap-3 mt-4 ${isEnabled("STRIPE_UI") ? "grid-cols-2" : "grid-cols-1"}`}>
            {isEnabled("STRIPE_UI") && (
              <Button
                data-testid={`pay-stripe-${dialogPlan?.id}`}
                onClick={() => {
                  const opts = dialogPlan?.id === "travel_pass" ? { days: travelDays } : {};
                  startStripe(dialogPlan.id, opts);
                }}
                disabled={loadingPlan === `stripe:${dialogPlan?.id}`}
                variant="outline"
                className="rounded-none uppercase tracking-[0.2em] text-xs h-11">
                {loadingPlan === `stripe:${dialogPlan?.id}`
                  ? <Loader2 size={14} className="animate-spin" />
                  : "Pay with Stripe"}
              </Button>
            )}
            <Button
              data-testid={`pay-razorpay-${dialogPlan?.id}`}
              onClick={() => {
                const opts = dialogPlan?.id === "travel_pass" ? { days: travelDays } : {};
                startRazorpay(dialogPlan.id, opts);
              }}
              disabled={loadingPlan === `razorpay:${dialogPlan?.id}` || !rpReady}
              className="rounded-none uppercase tracking-[0.2em] text-xs h-11">
              {loadingPlan === `razorpay:${dialogPlan?.id}`
                ? <Loader2 size={14} className="animate-spin" />
                : (rpReady ? "Pay with Razorpay" : "Razorpay · awaiting keys")}
            </Button>
          </div>
          {!rpReady && (
            <p className="text-[10px] text-muted-foreground mt-3">
              Razorpay activates the moment test keys are pasted into the backend&apos;s <code>.env</code>.
            </p>
          )}
        </DialogContent>
      </Dialog>

      {/* Feedback CTA — placed at the bottom of the pricing page so
          hesitant users have a channel to ask questions or share concerns. */}
      <div className="mt-16 border-t border-border pt-8 flex justify-center">
        <Link
          to="/feedback?from=plans"
          data-testid="plans-feedback-btn"
          className="inline-flex items-center gap-2 border border-border px-6 h-11 text-[11px] uppercase tracking-[0.22em] hover:border-accent hover:text-accent transition-colors"
        >
          <MessageSquare size={13} /> Send feedback
        </Link>
      </div>
    </div>
  );
}
